// Lo que cambió en un evento del historial de una regla, en líneas con los nombres del editor.
import type { RetailCommission } from '@fiado/type-kit';
import { formatDayMx } from '@/lib/dateHelpers';
import {
    describeCondition,
    describeLevers,
    describeScope,
    PAY_TIMING_LABEL,
    percent,
    STATUS_LABEL,
} from './ruleText';

type Change = RetailCommission.CommissionRuleChange;
type CountBy = RetailCommission.CommissionCountByEnum;

const FIELD_LABEL: Record<Change['field'], string> = {
    name: 'Nombre',
    description: 'Descripción',
    levers: 'Palancas',
    scope: 'Alcance',
    condition: 'Condición',
    effectiveFrom: 'Vigencia desde',
    effectiveTo: 'Vigencia hasta',
    priority: 'Prioridad',
    replacesOthers: 'Reemplaza a otras',
    countBy: 'Contar por',
    payTiming: 'Momento del pago',
    onSaleAdvanceBps: 'Adelanto al momento',
};

const COUNT_BY_LABEL: Record<CountBy, string> = {
    PIECE: 'Pieza',
    SALE: 'Venta (ticket)',
};

/** La etiqueta del valor; el valor tal cual si es uno nuevo que la pantalla no conoce. */
const labelOf = (labels: Partial<Record<string, string>>, value: string) => labels[value] ?? value;

/** Lo que contaba la regla antes y después del evento, para leer sus palancas y su condición. */
interface Counting {
    before?: CountBy;
    after?: CountBy;
}

function valuesOf(change: Change, counting: Counting): [string, string] {
    switch (change.field) {
        case 'name':
            return [change.before, change.after];
        case 'description':
            return [change.before || '—', change.after || '—'];
        case 'levers':
            return [
                describeLevers(change.before, counting.before),
                describeLevers(change.after, counting.after),
            ];
        case 'scope':
            return [describeScope(change.before), describeScope(change.after)];
        case 'condition':
            return [
                describeCondition(change.before, counting.before),
                describeCondition(change.after, counting.after),
            ];
        case 'effectiveFrom':
            return [formatDayMx(change.before), formatDayMx(change.after)];
        case 'effectiveTo': {
            const day = (value: string | null) => (value ? formatDayMx(value) : 'Permanente');
            return [day(change.before), day(change.after)];
        }
        case 'priority':
            return [String(change.before), String(change.after)];
        case 'replacesOthers':
            return [change.before ? 'Sí' : 'No', change.after ? 'Sí' : 'No'];
        case 'countBy':
            return [labelOf(COUNT_BY_LABEL, change.before), labelOf(COUNT_BY_LABEL, change.after)];
        case 'payTiming':
            return [
                labelOf(PAY_TIMING_LABEL, change.before),
                labelOf(PAY_TIMING_LABEL, change.after),
            ];
        case 'onSaleAdvanceBps': {
            const advance = (bps: number | null) => (bps === null ? 'Sin adelanto' : percent(bps));
            return [advance(change.before), advance(change.after)];
        }
        default:
            // Un campo nuevo del motor no tumba el historial.
            change satisfies never;
            return ['—', '—'];
    }
}

/** El cambio de estado y los campos editados del evento; vacío si no trae ninguno. */
export function ruleEventLines(event: RetailCommission.CommissionRuleEvent): string[] {
    const lines: string[] = [];
    if (event.statusChange) {
        const { from, to } = event.statusChange;
        lines.push(`Estado: ${labelOf(STATUS_LABEL, from)} → ${labelOf(STATUS_LABEL, to)}`);
    }
    const changes = event.changes ?? [];
    // Si la misma edición cambió lo que cuenta, el antes se lee con lo de antes.
    const countByChange = changes.find(
        (change): change is Extract<Change, { field: 'countBy' }> => change.field === 'countBy',
    );
    const counting = { before: countByChange?.before ?? event.countBy, after: event.countBy };
    for (const change of changes) {
        const [before, after] = valuesOf(change, counting);
        lines.push(`${FIELD_LABEL[change.field]}: ${before} → ${after}`);
    }
    return lines;
}
